import { Set } from 'immutable';
import { createSelector } from 'reselect';
import { getProduct, getOrderedFields, getOrderedFieldsArray, getWood, getDistress, getFinishOptions, getCustomOptions, selectedFieldOption, getWoodThumbnail, getDistressThumbnail, getDefaultProps, getDefaultPropsByFieldName, addSuffixForValidFieldValue } from './index';
import * as fieldName from '../constants/Fields';
import * as doorFieldName from '../constants/DoorFields';

const getDoorType = (state) => state.getIn(['product', 'doorType']);
const getInteriorExterior = (state) => state.getIn(['fields', doorFieldName.INTERIOR_EXTERIOR]);
const getFireRating = (state) => state.getIn(['fields', doorFieldName.FIRE_RATING]);
const getSingleDoubleValue = (state) => state.getIn(['fields', doorFieldName.SINGLE_DOUBLE, 'value']);
const getWidth = (state) => state.getIn(['fields', doorFieldName.WIDTH]);
const getHeight = (state) => state.getIn(['fields', doorFieldName.HEIGHT]);
const getThickness = (state) => state.getIn(['fields', doorFieldName.THICKNESS]);
const getGlass = (state) => state.getIn(['fields', doorFieldName.GLASS]);
const getProfile = (state) => state.getIn(['fields', doorFieldName.PROFILE]);
const getHandingSwing = (state) => state.getIn(['fields', doorFieldName.HANDING_SWING]);
const getJamb = (state) => state.getIn(['fields', doorFieldName.JAMB]);
const getJambWidth = (state) => state.getIn(['fields', doorFieldName.JAMB_WIDTH]);
const getHinge = (state) => state.getIn(['fields', doorFieldName.HINGE]);
const getMachining = (state) => state.getIn(['fields', doorFieldName.MACHINING]);

const getIsFireDoorSelected = createSelector(
  getInteriorExterior,
  interiorExterior => interiorExterior.get('value') && interiorExterior.get('value').toLowerCase().indexOf('fire') > -1
);

export const getIsDouble = createSelector(
  getSingleDoubleValue,
  singleDoubleValue => singleDoubleValue === 'Double Door'
);

export const getName = createSelector(
  [getProduct, getIsDouble],
  (door, isDouble) => isDouble ? door.get('doubleName') : door.get('name')
);

const getNoJambSelected = createSelector(
  getHandingSwing,
  handingSwing => {
    const handingSwingValue = handingSwing.get('value');
    return handingSwingValue && handingSwingValue.toLowerCase().indexOf('no jamb') > -1
  }
);

const getDoorWithoutHingeSelected = createSelector(
  getHandingSwing,
  handingSwing => {
    const nonHingeDoors = ['Slab Only (No Jamb)', 'Slab Only, Double (No Jamb)', 'Sliding Barn Door', 'Double Sliding Barn Door', 'Bi-Fold', 'Double Bi-Fold', 'Pocket', 'Double Pocket', 'Double Bi-Pass'];
    const handingSwingValue = handingSwing.get('value');
    return handingSwingValue && nonHingeDoors.indexOf(handingSwingValue) > -1;
  }
);

const getNoMachiningOptionSelected = createSelector(
  getHandingSwing,
  handingSwing => {
    const noMachiningHandingSwingOptions = ['Slab Only (No Jamb)', 'Right Hand Ball Catch, Inswing', 'Right Hand Ball Catch, Outswing', 'Left Hand Ball Catch, Inswing', 'Left Hand Ball Catch, Outswing', 'Slab Only, Double (No Jamb)', 'Double Ball Catch, Inswing', 'Double Ball Catch, Outswing', 'Sliding Barn Door', 'Double Sliding Barn Door', 'Bi-Fold', 'Double Bi-Fold', 'Pocket', 'Double Pocket', 'Double Bi-Pass'];
    const handingSwingValue = handingSwing.get('value');
    return handingSwingValue && noMachiningHandingSwingOptions.indexOf(handingSwingValue) > -1;
  }
);

const getInteriorExteriorOptions = createSelector(
  [getInteriorExterior, getProduct],
  (interiorExterior, door) => {
    const interiorExteriorOptions = interiorExterior.get('options');
    const doorUses = door.get('uses');
    window.interiorExteriorOptions = interiorExteriorOptions;
    const calculatedProps = {
      disabled: interiorExteriorOptions.filter(option => doorUses.indexOf(option.value.split(' ')[0].toLowerCase()) === -1).map(option => option.displayName)
    };

    return Object.assign({}, getDefaultProps(interiorExterior), calculatedProps);
  }
);

const getFireRatingOptions = createSelector(
  [getFireRating, getIsFireDoorSelected],
  (fireRating, isFireDoorSelected) => {
    const calculatedProps = {
      hide: !isFireDoorSelected,
      summaryText: isFireDoorSelected ? fireRating.get('value') : null
    };

    return Object.assign({}, getDefaultProps(fireRating), calculatedProps);
  }
);

const getWidthOptions = createSelector(
  [getWidth, getIsDouble],
  (width, isDouble) => {
    const widthOptions = width.get('options');
    let calculatedProps = {
      summaryText: addSuffixForValidFieldValue(width, ' Width')
    };

    const disableWidthValue = isDouble ?
      function( widthOptionValue ) { return widthOptionValue.indexOf('Double') < 0 } :
      function( widthOptionValue ) { return widthOptionValue.indexOf('Double') >= 0 };

    calculatedProps.disabled = widthOptions.map( widthOption => widthOption.value ).filter( widthOptionValue => disableWidthValue(widthOptionValue) );

    return Object.assign({}, getDefaultProps(width), calculatedProps);
  }
);

const getHeightOptions = createSelector(
  [getHeight, getIsFireDoorSelected],
  (height, isFireDoorSelected) => {
    let calculatedProps = {
      summaryText: addSuffixForValidFieldValue(height, ' Tall')
    };

    return Object.assign({}, getDefaultProps(height), calculatedProps);
  }
);

const getThicknessOptions = createSelector(
  [getThickness],
  (thickness) => {
    let calculatedProps = {
      summaryText: addSuffixForValidFieldValue(thickness, ' Thick')
    };

    return Object.assign({}, getDefaultProps(thickness), calculatedProps);
  }
);

const getDistressOptions = createSelector(
  [getDistress, getProduct, getWood, getName],
  (distress, door, wood, doorName) => {
    const woodValue = wood.get('value');
    const reclaimedWoods = ['Reclaimed Oak (White)', 'Reclaimed Barnwood'];
    const isReclaimedWood = reclaimedWoods.indexOf(woodValue) >= 0;
    const flushDoorSuffixRegex = /-F\d{1,3}(-PW?)?$/;
    const isFlushDoor = flushDoorSuffixRegex.test(doorName);
    let calculatedProps = {};

    if (isReclaimedWood || isFlushDoor) {
      calculatedProps.hide = true;
    } else {
      const distressOptions = distress.get('options');
      const doorName = door.get('name');
      const showAntiqueOption = doorName.substr(-1) === 'P' || doorName.substr(-2) === 'PW';
      const antiqueDistressOption = 'Antique Distress';

      calculatedProps.disabled = distressOptions.filter( distressOption => {
          const isAntiqueOption = distressOption.value === antiqueDistressOption;
          return showAntiqueOption ? !isAntiqueOption : isAntiqueOption;
        }).map( distressOption => distressOption.value );
    }

    return Object.assign({}, getDefaultProps(distress), calculatedProps);
  }
);

const getGlassOptions = createSelector(
  [getGlass, getInteriorExterior, getProduct],
  (glass, interiorExterior, door) => {
    const glassOptions = glass.get('options');
    const interiorExteriorValue = interiorExterior.get('value');
    const woodOnly = door.get('constructionFilters').indexOf('wood-only') > -1;
    let calculatedProps = {};

    if (woodOnly) {
      calculatedProps = { hide: true, selected: '', summaryText: null };
    } else {
      calculatedProps.summaryText = addSuffixForValidFieldValue(glass, ' Glass');
    }

    if (interiorExteriorValue) {
      switch(interiorExteriorValue) {
      case 'Interior Door':
        calculatedProps.disabled = glassOptions.filter( glassOption => (glassOption.sgorig.indexOf('sg') < 0 && glassOption.sgorig.indexOf('ig') < 0)).map( glassOption => glassOption.value );
        break;
      case 'Exterior Door':
        calculatedProps.disabled = glassOptions.filter( glassOption => glassOption.sgorig.indexOf('ig') < 0).map( glassOption => glassOption.value );
        break;
      case 'Fire Door':
        calculatedProps.disabled = glassOptions.filter( glassOption => glassOption.sgorig.indexOf('fire') < 0).map( glassOption => glassOption.value );
      }
    }

    return Object.assign({}, getDefaultProps(glass), calculatedProps);
  }
);

const getProfileOptions = createSelector(
  [getProfile, getProduct, getDoorType, getName],
  (profile, door, doorType, doorName) => {
    const profileOptions = profile.get('options');
    const isPlankDoor = doorName.substr(-1) === 'P' || doorName.substr(-2) === 'PW';
    const isGlassOnlyDoor = door.get('constructionFilters').indexOf('glass-only') > -1;
    const isFlushDoor = door.get('construction').indexOf('wood-flush-doors') >= 0;
    const isCraftsmanDoor = doorType === 'craftsman-wood-doors';
    const a1OnlyDoorSuffixRegex = /-(X{1,2}|HX|Z{1,2})$/;
    const a2OnlyDoorSuffixRegex = /-(X{1,2}P|HXP|Z{1,2}P)$/;
    const noProfileDoorTypes = ['wood-flush-doors', 'wood-louver-doors'];
    let calculatedProps = {};

    if (noProfileDoorTypes.indexOf(doorType) >= 0 || isFlushDoor) {
      calculatedProps.hide = true;
    } else if (isCraftsmanDoor) {
      if (isGlassOnlyDoor) {
        calculatedProps.disabled = profileOptions.filter( profileOption => profileOption.value !== 'A0' ).map( profileOption => profileOption.value );
      } else {
        const craftsmanDoorOptions = ['A1', 'A1E-MV'];
        calculatedProps.disabled = profileOptions.filter( profileOption => craftsmanDoorOptions.indexOf(profileOption.value) < 0 ).map( profileOption => profileOption.value );
      }
    } else if (a1OnlyDoorSuffixRegex.test(doorName)) {
      calculatedProps.disabled = profileOptions.filter( profileOption => profileOption.value !== 'A1' ).map( profileOption => profileOption.value );
    } else if (a2OnlyDoorSuffixRegex.test(doorName)) {
      calculatedProps.disabled = profileOptions.filter( profileOption => profileOption.value !== 'A2' ).map( profileOption => profileOption.value );
    } else if (isPlankDoor) {
      const plankOnlyOptions = ['A2', 'C2', 'A2-SM118', 'A2-SM313', 'A8-SM87'];
      calculatedProps.disabled = profileOptions.filter( profileOption => plankOnlyOptions.indexOf(profileOption.value) < 0 ).map( profileOption => profileOption.value );
    } else if (!isGlassOnlyDoor) {
      const glassOnlyOptions = ['C0', 'A0', 'O0', 'T0'];
      calculatedProps.disabled = profileOptions.filter( profileOption => glassOnlyOptions.indexOf(profileOption.value) >= 0 ).map( profileOption => profileOption.value );
    }

    calculatedProps.summaryText = addSuffixForValidFieldValue(profile, ' Profile');

    return Object.assign({}, getDefaultProps(profile), calculatedProps);
  }
);

const getHandingSwingOptions = createSelector(
  [getHandingSwing, getProduct, getIsDouble, getIsFireDoorSelected, getName],
  (handingSwing, door, isDouble, isFireDoorSelected, doorName) => {
    const handingSwingOptions = handingSwing.get('options');
    const archedDoorPrefixRegex = /^(FA|TR|EL|TD|GO|CH|FH|TH|EH|UH|GH)-/;
    const isArchedDoor = archedDoorPrefixRegex.test(doorName);
    const isDutchDoor = door.get('application').indexOf('wood-dutch-doors') > -1;
    const single_double = isDouble ? 'double' : 'single';
    let calculatedProps = {};

    if (isFireDoorSelected) {
      if (isDouble) {
        const doubleFireOptions = ['Slab Only, Double (No Jamb)', 'Double Left Hand Active, Inswing (with T-Astragal)', 'Double Left Hand Active, Outswing (with T-Astragal)', 'Double Right Hand Active, Inswing (with T-Astragal)', 'Double Right Hand Active, Outswing (with T-Astragal)'];
        calculatedProps = { disabled: handingSwingOptions.filter( handingSwingOption => doubleFireOptions.indexOf(handingSwingOption.value) < 0 ).map( handingSwingOption => handingSwingOption.value ) };
      } else {
        const singleFireOptions = ['Slab Only (No Jamb)', 'Left Hand, Inswing', 'Left Hand, Outswing', 'Right Hand, Inswing', 'Right Hand, Outswing'];
        calculatedProps = { disabled: handingSwingOptions.filter( handingSwingOption => singleFireOptions.indexOf(handingSwingOption.value) < 0 ).map( handingSwingOption => handingSwingOption.value ) };
      }
    } else if (isArchedDoor) {
      if (isDouble) {
        const doubleArchOptions = ['Slab Only, Double (No Jamb)', 'Double Left Hand Active, Inswing (with T-Astragal)', 'Double Left Hand Active, Outswing (with T-Astragal)', 'Double Right Hand Active, Inswing (with T-Astragal)', 'Double Right Hand Active, Outswing (with T-Astragal)'];
        calculatedProps = { disabled: handingSwingOptions.filter( handingSwingOption => doubleArchOptions.indexOf(handingSwingOption.value) < 0 ).map( handingSwingOption => handingSwingOption.value ) };
      } else {
        const singleArchOptions = ['Slab Only (No Jamb)', 'Left Hand, Inswing', 'Left Hand, Outswing', 'Right Hand, Inswing', 'Right Hand, Outswing'];
        calculatedProps = { disabled: handingSwingOptions.filter( handingSwingOption => singleArchOptions.indexOf(handingSwingOption.value) < 0 ).map( handingSwingOption => handingSwingOption.value ) };
      }
    } else {
      let disabledOptions = handingSwingOptions.filter(option => option.singleDouble.indexOf(single_double) === -1).map(option => option.value);

      // Disable all Dutch Doors if not configuring a Dutch Door
      if (!isDutchDoor) {
        const doubleDutchOptions = ['Double Dutch Door Left Hand Active, Inswing (with T-Astragal)', 'Double Dutch Door Left Hand Active, Outswing (with T-Astragal)', 'Double Dutch Door Right Hand Active, Inswing (with T-Astragal)', 'Double Dutch Door Right Hand Active, Outswing (with T-Astragal)'];
        const singleDutchOptions = ['Dutch Door Left Hand, Inswing', 'Dutch Door Right Hand, Outswing', 'Dutch Door Left Hand, Outswing', 'Dutch Door Right Hand, Inswing'];
        const alsoDisable = isDouble ? doubleDutchOptions : singleDutchOptions;
        disabledOptions = disabledOptions.concat(alsoDisable);
      }

      calculatedProps = { disabled: disabledOptions };
    }

    return Object.assign({}, getDefaultProps(handingSwing), calculatedProps);
  }
);

const getJambOptions = createSelector(
  [getJamb, getNoJambSelected, getDoorWithoutHingeSelected, getInteriorExterior, getIsFireDoorSelected],
  (jamb, noJambSelected, doorWithoutHingeSelected, interiorExterior, isFireDoorSelected) => {
    const interiorExteriorValue = interiorExterior.get('value');
    const exteriorSelected = interiorExteriorValue && interiorExteriorValue.toLowerCase().indexOf('exterior') > -1;
    let calculatedProps = {};

    if (noJambSelected || (doorWithoutHingeSelected && exteriorSelected)) {
      calculatedProps = {
        hide: true,
        summaryText: null
      };
    } else if (doorWithoutHingeSelected) {
      calculatedProps = {
        disabled: jamb.get('options').map( option => option.value ).filter( disabledOption => disabledOption !== 'Flat Jamb, No Stop' )
      };
    } else if (exteriorSelected || isFireDoorSelected) {
      const exteriorOptions = ['Rabbeted Jamb', 'Rabbeted Jamb, Kerfed One Side'];

      calculatedProps = {
        disabled: jamb.get('options').reduce(function(disabledOptions, option) {
          return exteriorOptions.indexOf(option.value) > -1 ? disabledOptions : disabledOptions.concat(option.value);
        }, [])
      };
    }

    return Object.assign({}, getDefaultProps(jamb), calculatedProps);
  }
);

const getJambWidthOptions = createSelector(
  [getJambWidth, getJamb, getNoJambSelected],
  (jambWidth, jamb, noJambSelected) => {
    const jambValue = jamb.get('value');
    const blankLabel = jambWidth.get('blank_label');
    let calculatedProps = {};

    if (noJambSelected) {
      calculatedProps = {
        hide: true,
        summaryText: null
      };
    } else {

      calculatedProps.summaryText = addSuffixForValidFieldValue(jambWidth, ' Jamb Width');

      if (jambValue && jambValue !== blankLabel) {
        let availableJambWidths = getAvailableJambWidths(jambValue);
        calculatedProps.disabled = jambWidth.get('options').map( jambWidthOption => jambWidthOption.value ).filter( jambWidthOptionValue => availableJambWidths.indexOf(jambWidthOptionValue) < 0 );
      }
    }

    return Object.assign({}, getDefaultProps(jambWidth), calculatedProps);
  }
);

const getHingeOptions = createSelector(
  [getHinge, getDoorWithoutHingeSelected],
  (hinge, doorWithoutHingeSelected) => {
    let calculatedProps = {};

    if (doorWithoutHingeSelected) {
      calculatedProps = {
        hide: true,
        summaryText: null
      };
    } else {
      calculatedProps = {
        summaryText: addSuffixForValidFieldValue(hinge, ' Hinge Color')
      };
    }

    return Object.assign({}, getDefaultProps(hinge), calculatedProps);
  }
);

const getMachiningOptions = createSelector(
  [getMachining, getNoMachiningOptionSelected],
  (machining, noMachiningOptionSelected) => {
    let calculatedProps = {};

    if (noMachiningOptionSelected) {
      calculatedProps = {
        hide: true,
        summaryText: null
      }
    }

    return Object.assign({}, getDefaultProps(machining), calculatedProps);
  }
);

export const getIllustration = createSelector(
  [getProduct, getIsDouble],
  (door, isDouble) => isDouble ? door.get('doubleImage') : door.get('image')
);

export const getDoorFieldProps = (state, name) => {
  switch (name) {
    case doorFieldName.INTERIOR_EXTERIOR:
      return getInteriorExteriorOptions(state);
    case doorFieldName.FIRE_RATING:
      return getFireRatingOptions(state);
    case doorFieldName.WIDTH:
      return getWidthOptions(state);
    case doorFieldName.HEIGHT:
      return getHeightOptions(state);
    case doorFieldName.THICKNESS:
      return getThicknessOptions(state);
    case fieldName.DISTRESS:
      return getDistressOptions(state);
    case doorFieldName.GLASS:
      return getGlassOptions(state);
    case doorFieldName.PROFILE:
      return getProfileOptions(state);
    case doorFieldName.HANDING_SWING:
      return getHandingSwingOptions(state);
    case doorFieldName.JAMB:
      return getJambOptions(state);
    case doorFieldName.JAMB_WIDTH:
      return getJambWidthOptions(state);
    case doorFieldName.HINGE:
      return getHingeOptions(state);
    case doorFieldName.MACHINING:getName
      return getMachiningOptions(state);
    case fieldName.FINISH:
      return getFinishOptions(state);
    case fieldName.CUSTOM:
      return getCustomOptions(state);
  }

  return getDefaultPropsByFieldName(state, name);
};

const getGlassThumbnail = createSelector(getGlass, glass => Object.assign({}, selectedFieldOption(glass), {label: 'Glass', class: 'glass'}));
const getProfileThumbnail = createSelector(getProfile, profile => Object.assign({}, selectedFieldOption(profile), {label: 'Panel, Sticking, Applied Moulding', class: 'profile'}));
const getHandingSwingThumbnail = createSelector(getHandingSwing, handingSwing => Object.assign({}, selectedFieldOption(handingSwing), {label: 'Handing and Swing', class: 'handing'}));
const getJambThumbnail = createSelector(getJamb, jamb => Object.assign({}, selectedFieldOption(jamb), {label: 'Jamb Type', class: 'jamb'}));
const getHingeThumbnail = createSelector(getHinge, hinge => Object.assign({}, selectedFieldOption(hinge), {label: 'Hinges', class: 'hinge'}));
const getMachiningThumbnail = createSelector(getMachining, machining => Object.assign({}, selectedFieldOption(machining), {label: 'Machining', class: 'machining'}));

export const getDoorSelectedOption = (state, name) => {
  switch(name) {
    case fieldName.WOOD:
      return getWoodThumbnail(state);
    case doorFieldName.GLASS:
      return getGlassThumbnail(state);
    case fieldName.DISTRESS:
      return getDistressThumbnail(state);
    case doorFieldName.PROFILE:
      return getProfileThumbnail(state);
    case doorFieldName.HANDING_SWING:
      return getHandingSwingThumbnail(state);
    case doorFieldName.JAMB:
      return getJambThumbnail(state);
    case doorFieldName.HINGE:
      return getHingeThumbnail(state);
    case doorFieldName.MACHINING:
      return getMachiningThumbnail(state);
  }
}

const getAvailableJambWidths = function(jambValue) {
  switch(jambValue) {
  case 'Flat Jamb, with Adjustable Stop':
    return ['4-5/8" (2x4, 1/2" Sheetrock)', '4-7/8" (2x4, 5/8" Sheetrock)', '6-5/8" (2x6, 1/2" Sheetrock)', '6-7/8" (2x6, 5/8" Sheetrock)'];
  case 'Flat Jamb, with Adjustable Stop, Kerfed One Side':
    return ['4-1/16" (Kerfed One Side, 2x4, 1/2" Sheetrock)', '4-3/16" (Kerfed One Side, 2x4, 5/8" Sheetrock)', '6-1/16" (Kerfed One Side, 2x6, 1/2" Sheetrock)', '6-3/16" (Kerfed One Side, 2x6, 5/8" Sheetrock)'];
  case 'Flat Jamb, with Adjustable Stop, Kerfed Both Sides':
    return ['3-1/2" (Kerfed Both Sides, 2x4)', '5-1/2" (Kerfed Both Sides, 2x6)'];
  case 'Rabbeted Jamb':
    return ['4-5/8" (2x4, 1/2" Sheetrock)', '4-7/8" (2x4, 5/8" Sheetrock)', '6-5/8" (2x6, 1/2" Sheetrock)', '6-7/8" (2x6, 5/8" Sheetrock)'];
  case 'Rabbeted Jamb, Kerfed One Side':
    return ['4-1/16" (Kerfed One Side, 2x4, 1/2" Sheetrock)', '4-3/16" (Kerfed One Side, 2x4, 5/8" Sheetrock)', '6-1/16" (Kerfed One Side, 2x6, 1/2" Sheetrock)', '6-3/16" (Kerfed One Side, 2x6, 5/8" Sheetrock)'];
  }

  return [];
}

export const getDoorSummaryFieldValues = (state) => {
  return getOrderedFieldsArray(state).map((name) => {
    const props = getDoorFieldProps(state, name);
    return (props.hide || props.disabled.indexOf(props.selected) >= 0) ? false : props.summaryText;
  }).filter((displayValue) => displayValue ? true : false);
};

export const getDoorOptionThumbnails = (state) => {
  const thumbnailFields = [fieldName.WOOD, doorFieldName.GLASS, fieldName.DISTRESS, doorFieldName.PROFILE, doorFieldName.HANDING_SWING, doorFieldName.JAMB, doorFieldName.HINGE, doorFieldName.MACHINING];
  return thumbnailFields.map((name) => {
    const fieldProps = getDoorFieldProps(state, name),
          disabledOptions = fieldProps.disabled,
          isDisabled = disabledOptions && disabledOptions.indexOf(fieldProps.selected) > -1,
          isHidden = fieldProps.hide === true;

    return (isDisabled || isHidden) ? {} : getDoorSelectedOption(state, name);
  }).filter(thumbnail => thumbnail.image ? true : false);
};
